
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import type { Word, LogicRule } from "@/lib/game-utils";

interface WordPreviewScreenProps {
  selectedWords: Array<{ word: Word; order: number }>;
  selectedRule: LogicRule;
  onRevealResults: () => void;
  previewMode?: boolean; // Add this prop to hide elements when used in modal
}

export function WordPreviewScreen({
  selectedWords,
  selectedRule,
  onRevealResults,
  previewMode = false // Default is false for standard game flow
}: WordPreviewScreenProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showScreenshotText, setShowScreenshotText] = useState(true);
  
  // Effect to fade out the screenshot text after 3 seconds
  useEffect(() => {
    if (!previewMode) {
      const timer = setTimeout(() => {
        setShowScreenshotText(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [previewMode]);
  
  // Sort words by their order
  const orderedWords = [...selectedWords].sort((a, b) => a.order - b.order);

  const handleRevealResults = () => {
    setIsLoading(true);
    // Add a small delay for better UX
    setTimeout(() => {
      onRevealResults();
    }, 500);
  };

  return (
    <div className={previewMode ? "" : "min-h-screen bg-gray-50 flex flex-col items-center justify-center py-8"}>
      <div className={previewMode ? "" : "container mx-auto px-4"}>
        {/* Only show these elements if NOT in previewMode (they're already in the modal) */}
        {!previewMode && (
          <>
            {/* Move text higher up with margin-bottom and fixed height */}
            <div className="flex justify-center mb-6 h-6">
              <div className="relative h-6 w-full">
                {showScreenshotText && (
                  <p className="text-xs text-gray-500 transition-opacity duration-500 absolute left-1/2 transform -translate-x-1/2 -top-4">
                    Take a screenshot to share later.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
        
        <div className={`flex justify-center ${previewMode ? "" : "mb-6"}`}>
          <div className="player-card p-6 w-full max-w-[16rem]">
            {/* Rule name */}
            <div className="text-center mb-0">
              <p className="text-lg font-bold text-gray-700">{selectedRule.name}</p>
              
              {/* Display spectrum if available - moved up by reducing top margin */}
              {selectedRule.spectrum && selectedRule.spectrum[0] && selectedRule.spectrum[1] && (
                <p className="text-sm text-gray-500 mt-0">
                  <span>{selectedRule.spectrum[0]}</span>
                  <span className="mx-1"><i>to</i></span>
                  <span>{selectedRule.spectrum[1]}</span>
                </p>
              )}
            </div>
            
            {/* Logo with two horizontal lines on each side */}
            <div className="logo-container">
              <div className="horizontal-lines">
                <div className="horizontal-line"></div>
                <div className="horizontal-line"></div>
              </div>
              <img 
                src="https://jasonvbenjamin.github.io/matchmylogic/logo_mml.svg" 
                alt="Match My Logic" 
                className="player-card-logo"
              />
              <div className="horizontal-lines">
                <div className="horizontal-line"></div>
                <div className="horizontal-line"></div>
              </div>
            </div>
            
            {/* Words in selected order - reduced space-y value from 3 to 1 */}
            <div className="flex flex-col items-center space-y-1">
              {orderedWords.map((item, index) => (
                <div key={index} className="text-3xl font-serif italic w-full text-center">
                  {item.word}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Only show reveal button if NOT in previewMode */}
        {!previewMode && (
          <div className="flex justify-center">
            <Button
              onClick={handleRevealResults}
              disabled={isLoading}
              className="flex items-center space-x-2 bg-black text-white hover:bg-gray-800 rounded-full px-8 py-6"
            >
              {isLoading ? "Loading..." : "Reveal Game Logic"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
